import type FileSystemItem from 'devextreme/file_management/file_system_item';
import { documentEditorHref } from '../documentEditor/documentEditorUrls';
import { getOfficePreviewKind } from '../documentEditor/documentFileTypes';
import { shareDocumentPreviewLink } from '../documentEditor/documentViewerActions';
import { downloadFileUrl } from './fileBrowserApi';
import { parseItemPathForRoutes, type MountRoute } from './createFileBrowserProvider';
import { getMediaKind } from '../mediaViewer/mediaFileTypes';
import { displayNameFromPath } from '../mediaViewer/mediaFileTypes';
import { shareMediaViewerLink } from '../mediaViewer/mediaViewerActions';
import { mediaViewerHref } from '../mediaViewer/mediaPreviewUrls';
import { isPdfFile } from '../pdfViewer/pdfFileTypes';
import { pdfViewerHref } from '../pdfViewer/pdfPreviewUrls';
import { sharePdfViewerLink } from '../pdfViewer/pdfViewerActions';
import { showErrorToast, showSuccessToast, showToast } from '../notifications/toast';

export const JARVIS_SHARE = 'jarvisShare';

async function shareUrl(url: string, title: string): Promise<void> {
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ url, title });
      return;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
    }
  }

  if (!navigator.clipboard?.writeText) {
    showErrorToast('Sharing is not supported in this browser.');
    return;
  }

  try {
    await navigator.clipboard.writeText(url);
    showSuccessToast('Link copied to clipboard.');
  } catch {
    showErrorToast('Could not copy the link.');
  }
}

export async function shareFileBrowserItem(item: FileSystemItem, routes: readonly MountRoute[]): Promise<void> {
  const parsed = parseItemPathForRoutes(item, routes);
  if (!parsed?.relative) {
    showToast('This item cannot be shared.', 'warning', 3000);
    return;
  }

  if (item.isDirectory) {
    showToast('Folder sharing is not available yet. Share individual files instead.', 'info', 4000);
    return;
  }

  const mediaKind = getMediaKind(item.name);
  if (mediaKind === 'image' || mediaKind === 'video') {
    await shareMediaViewerLink(mediaKind, parsed.mount.id, parsed.relative);
    return;
  }

  const officeKind = getOfficePreviewKind(item.name);
  if (officeKind) {
    await shareDocumentPreviewLink(officeKind, parsed.mount.id, parsed.relative);
    return;
  }

  if (isPdfFile(item.name)) {
    await sharePdfViewerLink(parsed.mount.id, parsed.relative);
    return;
  }

  const downloadUrl = new URL(downloadFileUrl(parsed.mount.id, parsed.relative), window.location.origin).href;
  await shareUrl(downloadUrl, displayNameFromPath(parsed.relative));
}

export function canShareFileBrowserItem(item: FileSystemItem | undefined): boolean {
  return Boolean(item && !item.isDirectory);
}

/** Deep link when a dedicated viewer exists; otherwise download URL. */
export function resolveShareHref(item: FileSystemItem, routes: readonly MountRoute[]): string | null {
  const parsed = parseItemPathForRoutes(item, routes);
  if (!parsed?.relative || item.isDirectory) return null;

  const mediaKind = getMediaKind(item.name);
  if (mediaKind === 'image' || mediaKind === 'video') {
    return new URL(mediaViewerHref(mediaKind, parsed.mount.id, parsed.relative), window.location.origin).href;
  }

  const officeKind = getOfficePreviewKind(item.name);
  if (officeKind) {
    return new URL(documentEditorHref(officeKind, 'preview', parsed.mount.id, parsed.relative), window.location.origin).href;
  }

  if (isPdfFile(item.name)) {
    return new URL(pdfViewerHref(parsed.mount.id, parsed.relative), window.location.origin).href;
  }

  return new URL(downloadFileUrl(parsed.mount.id, parsed.relative), window.location.origin).href;
}
